import { useCallback, useEffect, useMemo } from 'react';

import { useUserPrint } from '@app/hooks';
import { useMarkLatestRegistryResultStale } from '@app/pages/Leads/model';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { useTaskActions } from './useTaskActions';

import type { BaseState } from './useBaseState';

type UseLeadCardAddTaskParams = Pick<
  BaseState,
  'leadCardData' | 'refetchLeadCard' | 'selectedBranchId' | 'selectedLeadId'
>;

interface UseLeadCardAddTaskResult {
  openTaskModal(): void;
}

export const useAddTask = ({
  leadCardData,
  refetchLeadCard,
  selectedBranchId,
  selectedLeadId,
}: UseLeadCardAddTaskParams): UseLeadCardAddTaskResult => {
  const { createTaskForLead, closeTaskDetailsModal, onChangeSuccess } =
    useTaskActions();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const markRegistryResultStale = useMarkLatestRegistryResultStale();

  const clientId = useMemo(
    () => leadCardData?.ClientId,
    [leadCardData?.ClientId]
  );

  // A task created/completed/edited from the Lead Card flows through the shared
  // task provider's single success callback. Besides refetching the card,
  // flag the Leads Registry result as stale so it refetches on return and
  // reflects the updated task state without a manual page refresh.
  const handleTaskChangeSuccess = useCallback(() => {
    refetchLeadCard();
    markRegistryResultStale();
  }, [markRegistryResultStale, refetchLeadCard]);

  useEffect(() => {
    onChangeSuccess(handleTaskChangeSuccess);

    return () => onChangeSuccess(() => {});
  }, [handleTaskChangeSuccess, onChangeSuccess]);

  useEffect(() => {
    closeTaskDetailsModal();
  }, [closeTaskDetailsModal, selectedBranchId, selectedLeadId]);

  const openTaskModal = useCallback(() => {
    if (!clientId) {
      showNotification('Идентификатор клиента не найден', 'frontWarning');
      return;
    }

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', 'frontWarning');
      return;
    }

    createTaskForLead({
      ClientId: clientId,
    });
  }, [ActiveBranchId, createTaskForLead, clientId, showNotification]);

  return useMemo(
    () => ({
      openTaskModal,
    }),
    [openTaskModal]
  );
};
